import { Component, inject, Input, OnInit, signal } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { ButtonComponent } from '../../../shared-with-client/button/button.component';
import { IconComponent } from '../../../shared-with-client/icon/icon.component';
import { MerchantCurrencyPipe } from '../../../shared/pipes/merchant-currency.pipe';
import { InvoicingService } from '../../../core/services/invoicing.service';
import { ApiGatewayService } from '../../../core/services/api-gateway.service';
import { DatePipe } from '@angular/common';

export enum SaleDetailsDocumentType {
    INVOICE = 'invoice',
    PAYMENT = 'payment',
    CREDITNOTE = 'creditNote'
}

@Component({
    selector: 'app-sale-detail-documents',
    imports: [IconComponent, MerchantCurrencyPipe, ButtonComponent, TranslatePipe, DatePipe],
    templateUrl: './sale-detail-documents.component.html',
    styleUrl: './sale-detail-documents.component.scss',
})
export class SaleDetailDocumentsComponent implements OnInit {
    @Input() sale: any;

    private api = inject(ApiGatewayService);
    private invoicingService = inject(InvoicingService);

    public saleHasDocuments: boolean = false;

    public saleDocuments = signal<any[]>([]);

    SaleDetailsDocumentType = SaleDetailsDocumentType;

    ngOnInit(): void {
        this.saleHasDocuments = this.hasDocuments();
        this.saleDocuments.set(this.transformSaleDocuments(this.sale) || []);
    }

    downloadInvoice(pdfUrl: any, documentReference?: string): void {
        const filename = `buk_${documentReference ?? this.sale.invoiceReference}.pdf`;
        this.api.downloadUrlAsFile(pdfUrl, filename);
    }

    emailInvoice(saleInvoiceId: number): void {
        this.invoicingService.requestEmailInvoice({
            merchantClientEmail: this.sale.merchantClient?.email,
            saleId: this.sale.id,
            saleInvoiceId,
        });
    }

    emailReceipt(salePaymentId: number): void {
        this.invoicingService.requestEmailInvoice({
            merchantClientEmail: this.sale.merchantClient?.email,
            saleId: this.sale.id,
            salePaymentId,
        });
    }

    emailCreditNote(creditNoteId: number): void {
        this.invoicingService.requestEmailInvoice({
            merchantClientEmail: this.sale.merchantClient?.email,
            saleId: this.sale.id,
            creditNoteId,
        });
    }

    transformSaleDocuments(saleData: any) {
        const invoicesGroups = saleData.saleInvoices?.map((inv: any) => {
            return {
                id: inv.id,
                createdAt: inv.createdAt,
                invoiceReference: inv.invoiceReference,
                amount: inv.amount
            }
        })?.sort((a: any, b: any) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()) || [];

        const invoices = saleData.saleInvoices?.map((inv: any) => {return {...inv, documentType: SaleDetailsDocumentType.INVOICE }}) || [];
        const payments = saleData.salePayments?.map((pay: any) => {return {...pay, documentType: SaleDetailsDocumentType.PAYMENT }}) || [];
        const creditNotes = saleData.creditNotes?.map((cn: any) => {return {...cn, documentType: SaleDetailsDocumentType.CREDITNOTE }}) || [];

        const invoicesGroupsWithDocuments = invoicesGroups.map((invoiceGroup: any) => {
            const documents: any = [];
            documents.push(...invoices.filter((inv: any) => inv.id === invoiceGroup.id));
            documents.push(...payments.filter((pay: any) => pay.saleInvoiceId === invoiceGroup.id));
            documents.push(...creditNotes.filter((cn: any) => cn.saleInvoiceId === invoiceGroup.id));
            // documents.sort((a: any, b: any) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
            return {
                invoiceId: invoiceGroup.id,
                invoiceCreatedAt: invoiceGroup.createdAt,
                invoiceReference: invoiceGroup.invoiceReference,
                amount: invoiceGroup.amount,
                documents: documents
            };
        });

        return invoicesGroupsWithDocuments;
    }


    hasDocuments(): boolean {
        const hasReceipts = this.sale?.salePayments?.some(payment => payment.receiptPdfUrl);
        const hasCreditNotes = this.sale?.creditNotes?.some(creditNote => creditNote.pdfUrl);
        const hasInvoices = this.sale?.saleInvoices?.some((inv: any) => inv.invoicePdfUrl);
        return this.sale.invoicePdfUrl || hasReceipts || hasCreditNotes || hasInvoices;
    }
}
